import React from 'react';

import { ChevronLeft, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { Separator } from '@/components/ui/separator';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';

interface BackButtonProps {
  text: string;
  onClick: () => void;
  isDisabled?: boolean;
}

interface NextButtonProps {
  text: string;
  onClick: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  disabledTooltip?: string;
}

interface OnboardingBottomSectionProps {
  backButtonProps?: BackButtonProps;
  nextButtonProps: NextButtonProps;
}

export const OnboardingBottomSection: React.FC<OnboardingBottomSectionProps> = ({
  backButtonProps,
  nextButtonProps,
}) => {
  const { t } = useTranslation('common');
  return (
    <div className="fixed bottom-0 left-0 z-10 flex w-full flex-row">
      <div className="w-[24rem] max-lg:w-[18rem] max-md:w-[12rem]" />
      <div className="flex-1 bg-background">
        <Separator className="mt-0" />
        <div className="flex flex-row justify-end gap-4 px-12 py-3 max-md:px-6">
          {backButtonProps && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={backButtonProps.onClick}
                disabled={backButtonProps.isDisabled ?? false}
                className="hidden md:inline-flex"
              >
                {backButtonProps.text}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={backButtonProps.onClick}
                disabled={backButtonProps.isDisabled ?? false}
                className="md:hidden"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </>
          )}
          <div className="flex-1" />
          {nextButtonProps.disabledTooltip ? (
            <SimpleTooltip content={<p>{nextButtonProps.disabledTooltip}</p>}>
              <span className="inline-flex">
                <ButtonLoadable
                  type="button"
                  onClick={nextButtonProps.onClick}
                  disabled={nextButtonProps.isDisabled}
                  isLoading={nextButtonProps.isLoading}
                  icon={<Info className="text-current/80" />}
                >
                  {nextButtonProps.text}
                </ButtonLoadable>
              </span>
            </SimpleTooltip>
          ) : (
            <ButtonLoadable
              type="button"
              onClick={nextButtonProps.onClick}
              disabled={nextButtonProps.isDisabled}
              isLoading={nextButtonProps.isLoading}
            >
              {nextButtonProps.text}
            </ButtonLoadable>
          )}
        </div>
      </div>
    </div>
  );
};
